<template>
  <aside
:class="[ 'flex flex-col bg-[#F9F9F9] dark:bg-slate-900 fixed top-0 z-40 left-0 h-full bottom-0 overflow-y-auto overflow-x-visible transition-all w-56', isCollapsed ? 'translate-x-[-100%] sm:w-24 sm:translate-x-0' : '', ]" aria-label="Sidebar">
    <div
:class="[ 'flex items-center py-4 mx-4 border-b justify-between', isCollapsed ? 'sm:justify-center' : '', ]">
      <img
src="/images/logo-sm.png" alt="Small Logo" :class="[ 'pointer-events-none size-5 dark:brightness-0 dark:invert', isCollapsed ? 'hidden sm:block' : 'sm:hidden', ]">
      <img alt="Logo" src="/images/logo.png" :class="['h-5 pointer-events-none dark:brightness-0 dark:invert', isCollapsed ? 'sm:hidden' : '']">

      <Button
variant="secondary" size="lg" title="Toggle Sidebar" class="w-8 min-w-8 h-8 !p-0 justify-center"
        :aria-expanded="!isCollapsed" aria-controls="sidebar-content" :class="{ 'sm:ml-5': isCollapsed }"
        @click="$emit('toggleSidebar')">
        <RxCross1 :size="18" class="block text-neutral-400 size-4 sm:hidden" />
        <PiCodeSimpleBold :size="20" class="hidden text-neutral-400 size-4 sm:block" />
      </Button>
    </div>

    <ul
:id="'sidebar-content'" :class="[ 'w-full mt-2', isCollapsed ? 'sm:flex sm:flex-col sm:items-center' : '', ]">
      <li v-for="item in SIDEBAR_DATA" :key="item.title">
        <NuxtLink
v-slot="{ isActive }" :to="item.to" :active-class="'active'" :class="[ 'flex group items-center px-4 py-3 transition-colors cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800', isCollapsed ? 'sm:justify-center' : '', ]" :aria-label="item.title">
          <component
:is="item.icon" size="24" :class="[ 'rounded-md size-6', isActive ? 'bg-green-50 p-1 border border-green-400 scale-[1.35]' : '', isCollapsed ? 'mr-2.5 sm:mr-0' : 'mr-2.5', ]" stroke-class="stroke-[#39404F]" />
          <span
:class="[ 'text-sm font-medium text-[#39404F] dark:text-slate-100 font-manrope', isCollapsed ? 'sm:hidden' : '', ]">{{ item.title }}</span>
        </NuxtLink>
      </li>
    </ul>

    <NuxtLink
v-slot="{ isActive }" to="/notifications" :class="[ 'flex items-center mt-auto mx-4 py-3 relative transition-colors cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800', isCollapsed ? 'sm:justify-center' : '', ]" aria-label="Notifications">
      <HiOutlineBell
:class="[ 'stroke-[#39404F] size-6 rounded-md', isActive ? 'bg-green-50 p-1 border border-green-400 scale-[1.35]' : '', isCollapsed ? 'mr-2.5 sm:mr-0' : 'mr-2.5', ]" />
      <span
:class="[ 'text-sm font-medium text-[#39404F] font-manrope', isCollapsed ? 'sm:hidden' : '', ]">{{ $t('navigation.notifications') }}</span>
      <span
        v-if="notificationStore.hasUnread"
        class="absolute px-1 text-xs font-medium text-white rounded-full top-2 left-2.5 font-manrope bg-danger"
        aria-label="Unread notifications"
      >{{ notificationStore.unreadCount }}</span>
    </NuxtLink>
    <div
:class="[ 'relative flex items-center px-1 py-3 mx-3 border-t transition-transform cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800', isCollapsed ? 'sm:justify-center' : '', ]" aria-label="User Profile">
      <div class="flex items-center justify-center overflow-hidden rounded-full pointer-events-none size-10 profile-button min-w-10 bg-[#DDEEE7] text-[#1D6B4F] font-semibold uppercase">
        {{ profileInitials }}
      </div>

      <div :class="['ml-2', isCollapsed ? 'sm:hidden' : 'block']">
        <h5 class="text-sm font-semibold text-neutral-600 font-manrope">
          {{ profileName }}
        </h5>
        <p class="text-sm font-medium text-neutral-400 font-manrope max-w-full">
          {{ profileEmail }}
        </p>
      </div>

      <div class="absolute top-0 left-0 h-full w-full" @click="toggleProfileList" />
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <div
v-if="showProfileOptions" class="fixed z-50 w-56 bg-white dark:bg-slate-900 rounded-lg base-shadow profile-options"
          :style="{ bottom: location.top, left: location.left }">
          <div class="relative block w-full h-full p-3 py-2">
            <button
              class="absolute flex items-center justify-center w-8 h-8 cursor-pointer top-1 right-1 hover:opacity-75"
              type="button" @click="toggleProfileList">
              <RxCross1 :size="18" class="text-neutral-400 size-4" />
            </button>
            <div class="flex items-center w-full py-3" aria-label="User Profile">
              <div class="flex items-center justify-center overflow-hidden rounded-full pointer-events-none size-10 min-w-10 bg-[#DDEEE7] text-[#1D6B4F] font-semibold uppercase">
                {{ profileInitials }}
              </div>

              <div class="ml-2">
                <h5 class="text-sm font-semibold text-neutral-600 font-manrope">
                  {{ profileName }}
                </h5>
                <p class="text-sm font-medium text-neutral-400 font-manrope">
                  {{ profileEmail }}
                </p>
              </div>
            </div>

            <ul id="sidebar-content" class="w-full">
              <li
                v-for="item in PROFILE_MENU_DATA" :key="item.title" class="first:border-b first:mb-2"
                @click="item.action">
                <NuxtLink
                  v-slot="{ isActive }" :to="item.to" :class="[ 'flex items-center px-1.5 py-3 transition-colors cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800', ]" :aria-label="item.title">
                  <component
                    :is="item.icon" size="12" :class="[ 'w-5 h-5 mr-3 rounded-md', isActive ? 'bg-green-50 p-1 border border-green-400 scale-[1.5]' : '', ]" stroke-class="stroke-[#39404F]" />
                  <span class="text-sm font-medium text-[#39404F] dark:text-slate-100 font-manrope">{{ item.title }}</span>
                </NuxtLink>
              </li>
            </ul>

            <!-- Appearance & Language — sits above Log Out -->
            <div class="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-0.5">
              <!-- Theme toggle -->
              <div class="flex items-center justify-between px-1.5 py-2 rounded-lg">
                <span class="text-xs font-medium text-neutral-400 font-manrope uppercase tracking-wide">Appearance</span>
                <div class="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                  <button
                    type="button"
                    :class="['flex items-center justify-center w-7 h-6 rounded transition-colors', colorMode.preference === 'light' ? 'bg-white dark:bg-slate-900 shadow-sm text-amber-500' : 'text-neutral-400 hover:text-neutral-600']"
                    aria-label="Light mode"
                    @click="setThemePreference('light')"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  </button>
                  <button
                    type="button"
                    :class="['flex items-center justify-center w-7 h-6 rounded transition-colors', colorMode.preference === 'dark' ? 'bg-white dark:bg-slate-900 shadow-sm text-indigo-500' : 'text-neutral-400 hover:text-neutral-600']"
                    aria-label="Dark mode"
                    @click="setThemePreference('dark')"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                  </button>
                  <button
                    type="button"
                    :class="['flex items-center justify-center px-2 h-6 rounded transition-colors text-xs font-medium font-manrope', colorMode.preference === 'system' ? 'bg-white dark:bg-slate-900 shadow-sm text-neutral-700' : 'text-neutral-400 hover:text-neutral-600']"
                    aria-label="System mode"
                    @click="setThemePreference('system')"
                  >
                    Auto
                  </button>
                </div>
              </div>

              <!-- Language selector -->
              <div class="flex items-center justify-between px-1.5 py-2 rounded-lg">
                <span class="text-xs font-medium text-neutral-400 font-manrope uppercase tracking-wide">Language</span>
                <div class="relative">
                  <select
                    :value="locale"
                    class="appearance-none bg-slate-100 dark:bg-slate-800 text-sm font-medium text-[#39404F] dark:text-slate-100 font-manrope pl-2.5 pr-7 py-1 rounded-md outline-none cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    @change="onLanguageChange"
                  >
                    <option v-for="loc in locales" :key="loc.code" :value="loc.code">
                      {{ loc.name || loc.code.toUpperCase() }}
                    </option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-neutral-400">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>

              <div class="px-1.5 py-1">
                <AppVersionBadge />
              </div>
            </div>

            <!-- Log Out — separated at the very bottom -->
            <div class="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <NuxtLink
                to="/auth/logout"
                class="flex items-center px-1.5 py-3 transition-colors cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                aria-label="Log out"
                @click="logOut"
              >
                <LogoutIcon size="12" class="w-5 h-5 mr-3 rounded-md" stroke-class="stroke-[#39404F]" />
                <span class="text-sm font-medium text-[#39404F] dark:text-slate-100 font-manrope">{{ t('navigation.logOut') }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<script setup lang="ts">
import { useNotificationStore } from "~/stores/notificationStore";
import { PiCodeSimpleBold } from "vue3-icons/pi";
import { HiOutlineBell } from "vue3-icons/hi2";
import { RxCross1 } from "vue3-icons/rx";
import Button from "~/components/common/Button.vue";

import DoubleUserIcon from "./icons/DoubleUserIcon.vue";
import DashboardIcon from "./icons/DashboardIcon.vue";
// import MessageIcon from "./icons/MessageIcon.vue";
// import UsersIcon from "./icons/UsersIcon.vue";
import UserCheckIcon from "./icons/UserCheckIcon.vue";
import CallIcon from "./icons/CallIcon.vue";
import UserIcon from "./icons/UserIcon.vue";
import WalletIcon from "./icons/WalletIcon.vue";
import ListIcon from "./icons/ListIcon.vue";
import GlobeIcon from "./icons/GlobeIcon.vue";
import LogoutIcon from "./icons/LogoutIcon.vue";

import { logOut } from "@/services/authentication";
import { useColorMode, useI18n } from "#imports";
const notificationStore = useNotificationStore();

const userStore = useUserStore();
defineProps<{ isCollapsed: boolean }>();
defineEmits(["toggleSidebar"]);

const userDetails = computed(() => userStore.userDetails);
const hasHydrated = ref(false);

const profileName = computed(() => {
  if (!hasHydrated.value) {
    return "";
  }

  const firstName = userDetails.value.firstName?.trim() || "";
  const lastName = userDetails.value.lastName?.trim() || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || "Unknown user";
});

const profileEmail = computed(() => {
  if (!hasHydrated.value) {
    return "";
  }

  return shortenEmail(userDetails.value.email || "", 18);
});

const profileInitials = computed(() => {
  if (!hasHydrated.value) {
    return "";
  }

  const firstInitial = userDetails.value.firstName?.trim().charAt(0) || "";
  const lastInitial = userDetails.value.lastName?.trim().charAt(0) || "";
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();

  return initials || "?";
});

const location = ref({
  top: "0px",
  left: "0px",
});

const showProfileOptions = ref(false);

const colorMode = useColorMode();
const { t, locale, locales, setLocale } = useI18n();
type SupportedLocale = Parameters<typeof setLocale>[0];

const supportedLocales = computed<SupportedLocale[]>(() =>
  locales.value.map((loc) => loc.code as SupportedLocale)
);

const setThemePreference = (theme: "light" | "dark" | "system") => {
  colorMode.preference = theme;

  if (!import.meta.client) return;

  const html = document.documentElement;
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const shouldUseDark = theme === "dark" || (theme === "system" && prefersDark);
  html.classList.toggle("dark", shouldUseDark);
};

const onLanguageChange = (event: Event) => {
  const nextLocale = (event.target as HTMLSelectElement).value;

  if (!supportedLocales.value.includes(nextLocale as SupportedLocale)) {
    return;
  }

  setLocale(nextLocale as SupportedLocale);
};

const SIDEBAR_DATA = computed(() => [
  {
    title: t('navigation.dashboard'),
    icon: DashboardIcon,
    to: "/dashboard",
  },
  // {
  //   title: t('navigation.teams'),
  //   icon: UsersIcon,
  //   to: "/teams",
  // },
  {
    title: t('navigation.calls'),
    icon: CallIcon,
    to: "/calls",
  },
  {
    title: t('navigation.contacts'),
    icon: DoubleUserIcon,
    to: "/contacts",
  },
  {
    title: t('navigation.numbers'),
    icon: ListIcon,
    to: "/numbers",
  },
  // {
  //   title: t('navigation.messages'),
  //   icon: MessageIcon,
  //   to: "/messages",
  // },
]);

const PROFILE_MENU_DATA = computed(() => [
  {
    title: t('navigation.account'),
    icon: UserIcon,
    to: "/account/user-profile",
    action: () => { },
  },
  {
    title: t('navigation.billing'),
    icon: WalletIcon,
    to: "/billing",
    action: () => { },
  },
  {
    title: t('navigation.registrations'),
    icon: UserCheckIcon,
    to: "/number-registrations",
    action: () => { },
  },
  {
    title: t('navigation.support'),
    icon: GlobeIcon,
    to: "/support",
    action: () => { },
  },
]);  // Log Out is rendered separately below the preferences section

const toggleProfileList = (event: MouseEvent) => {
  if (showProfileOptions.value == true) {
    document.removeEventListener("click", handleOutsideClick);
    showProfileOptions.value = false;
    return;
  }

  const top = (event.target as HTMLElement).getBoundingClientRect().top;
  const left = (event.target as HTMLElement).getBoundingClientRect().left;
  const fromBottom = window.innerHeight - top + 4;

  location.value = {
    top: `${fromBottom}px`,
    left: `${left}px`,
  };

  showProfileOptions.value = true;
  setTimeout(() => {
    document.addEventListener("click", handleOutsideClick);
  }, 0);
};

const handleOutsideClick = (event: MouseEvent) => {
  if (
    showProfileOptions.value &&
    !document.querySelector(".profile-options")?.contains(event.target as Node)
  ) {
    toggleProfileList(event);
  }
};

const shortenEmail = (email: string, length: number) => {
  if (!email) {
    return email;
  }

  const atIndex = email.indexOf("@");
  const lastDotIndex = email.lastIndexOf(".");

  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1, lastDotIndex);
  const domainExtension = email.slice(lastDotIndex);

  if (email.length <= length) {
    return email;
  }

  let trimmedLocal = localPart;
  let trimmedDomain = domainPart;

  const remainingLength = length - (domainExtension.length + 1);

  const localAvailableLength = Math.max(remainingLength - domainPart.length - 1, 2)
  const domainAvailableLength = Math.max(remainingLength - localAvailableLength, 1);

  if (localPart.length > localAvailableLength) {
    trimmedLocal = localPart.slice(0, localAvailableLength - 1) + "*";
  }

  if (domainPart.length > domainAvailableLength) {
    trimmedDomain = domainPart.slice(0, domainAvailableLength - 1) + "*";
  }

  return trimmedLocal + "@" + trimmedDomain + domainExtension;
};

onMounted(() => {
  hasHydrated.value = true;
});

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translate(0, -10px);
}
</style>
